import { AD_INCATIVE_RESET_TIME } from '../../../Constants/Ads';
import { IClaimedDailyLoginReward } from '../../../Constants/DailyLogin';
import { DAILY_LOGIN_UNLOCK_LEVEL } from '../../../Constants/Level';
import { FIRST_LOGIN_DAYS_TO_TRACK } from '../../../Constants/Tracking/Tracking';
import Game from '../../../Game';
import { FTUXType } from '../../../Player/PlayerProperty/Progress/FTUXType';
import { report } from '../../../report';
import { IGameState } from '../../../State/Game/IGameState';
import UIStateHelper from '../../../State/UI/UIStateHelper';
import { TimerType } from '../../../System/Timer/TimerTypes';
import DailyLoginHelper from '../../../Utility/DailyLoginHelper';
import DateHelper from '../../../Utility/DateHelper';
import { MathHelper } from '../../../Utility/MathHelper';
import { IRewardData } from '../../../Utility/RewardHelper';
import { TActionType, TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { trackResourceEvent } from '../../../Utility/Tracking/TrackingHelpers/Transaction';
import { IPlayerState } from '../IPlayerState';
import { PlayerStateHelper } from '../PlayerStateHelper';
import { PlayerTask } from '../PlayerTasks';
import { InventoryStateHelper } from '../Inventory/InventoryStateHelper';
import { PopupID } from '../../UI/PopupID';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';

export namespace LoginStateHelper {
    export function checkDailyLogin(state: IGameState): IGameState {
        const lastLogin: Date = state.playerState.timers.get(TimerType.LAST_LOGIN);

        /**
         * If time since last played > duration reset ad cooldown timer
         */
        if (lastLogin && DateHelper.timeSince(lastLogin) >= AD_INCATIVE_RESET_TIME) {
            state = PlayerStateHelper.completeAdCooldownTimer(state);
        }

        const { todayClaimWindowEnd, lastClaimWindowEnd, lastClaimWindowStart } = DailyLoginHelper.getClaimWindows();

        const isFirstLoginOfDay = !lastLogin || lastLogin.getTime() <= lastClaimWindowEnd.getTime();
        const lastLoginWasYesterday =
            lastLogin &&
            MathHelper.isWithinRange(lastLogin.getTime(), lastClaimWindowStart.getTime(), lastClaimWindowEnd.getTime());
        const hasClaimedYesterdaysReward = state.playerState.progress.dailyLoginRewardsClaimed.some(claimedRewardData =>
            MathHelper.isWithinRange(
                claimedRewardData.date?.getTime() ?? 0,
                lastClaimWindowStart.getTime(),
                lastClaimWindowEnd.getTime()
            )
        );

        console.log(
            `
now: ${DateHelper.now}
lastLogin: ${lastLogin}
todayClaimWindowEnd: ${todayClaimWindowEnd}
lastClaimWindowStart: ${lastClaimWindowStart}
lastClaimWindowEnd: ${lastClaimWindowEnd}
isFirstLoginOfDay: ${isFirstLoginOfDay}
lastLoginWasYesterday: ${lastLoginWasYesterday}
`
        );

        // If last login was yesterday (but reward was NOT collected)
        if (lastLoginWasYesterday && !hasClaimedYesterdaysReward) {
            // Break login streak and reset daily session count
            state = LoginStateHelper.breakLoginStreak(state);

            // If last login was yesterday (and reward was collected)
        } else if (lastLoginWasYesterday) {
            // Increase login streak
            state = LoginStateHelper.setLoginStreak(state, state.playerState.progress.loginStreak + 1);
            // Reset daily session count
            state = LoginStateHelper.setDailySessions(state, 1);

            // If first log in of the day and player had not logged in yesterday then break streak
        } else if (isFirstLoginOfDay) {
            state = LoginStateHelper.breakLoginStreak(state);
        } else if (
            // If not the first log in of the day and reward can still be collected reshow popup
            DailyLoginHelper.canCollectDailyLoginReward(state.playerState)
        ) {
            state = LoginStateHelper.queueDailyLoginPopupNext(state);
        }

        // Update timer with today's date
        return TimerStateHelper.setTimer(state, TimerType.LAST_LOGIN, DateHelper.now);
    }

    export function incrementSessionCount(state: IGameState): IGameState {
        // Increase total amount of sessions
        state = LoginStateHelper.setTotalSessions(state, state.playerState.totalSessionCount + 1);
        state = LoginStateHelper.setDailySessions(state, state.playerState.sessionCount + 1);
        return state;
    }

    export function setDailySessions(state: IGameState, value: number): IGameState {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                sessionCount: value,
            },
        };
    }

    export function setTotalSessions(state: IGameState, value: number): IGameState {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                totalSessionCount: value,
            },
        };
    }

    export function setLoginStreak(oldState: IGameState, value: number): IGameState {
        let state = {
            ...oldState,
            playerState: {
                ...oldState.playerState,
                progress: {
                    ...oldState.playerState.progress,
                    loginStreak: value,
                },
            },
        };

        // Hide popup if already open
        if (state.UIState.popupID === PopupID.DAILY_LOGIN_MENU) {
            state = {
                ...state,
                UIState: UIStateHelper.hidePopup(state.UIState, PopupID.DAILY_LOGIN_MENU),
            };
        }

        // Queue next daily login popup
        return LoginStateHelper.queueDailyLoginPopupNext(state);
    }

    export function addClaimedDailyLoginReward(
        state: IPlayerState,
        collectedRewardData: IClaimedDailyLoginReward
    ): IPlayerState {
        return {
            ...state,
            progress: {
                ...state.progress,
                dailyLoginRewardsClaimed: [...state.progress.dailyLoginRewardsClaimed, collectedRewardData],
            },
        };
    }

    export function clearClaimedDailyLoginRewards(state: IPlayerState): IPlayerState {
        return {
            ...state,
            progress: {
                ...state.progress,
                dailyLoginRewardsClaimed: [],
            },
        };
    }

    /**
     * Resets both the login streak and daily sessions to 1 and clears all past claimed rewards
     */
    export function breakLoginStreak(oldState: IGameState): IGameState {
        let state = LoginStateHelper.setLoginStreak(oldState, 1);
        state = {
            ...state,
            playerState: LoginStateHelper.clearClaimedDailyLoginRewards(state.playerState),
        };
        return LoginStateHelper.setDailySessions(state, 1);
    }

    export function queueDailyLoginPopupNext(state: IGameState): IGameState {
        const lastLevelUnlocked = Game.player.getHighestLevelCompleted(state);
        if (lastLevelUnlocked >= DAILY_LOGIN_UNLOCK_LEVEL) {
            return {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(
                    state.playerState,
                    [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.DAILY_LOGIN_MENU,
                        },
                    ],
                    true
                ),
            };
        }
        return state;
    }

    export function setFirstLoadTimer(state: IGameState): IGameState {
        const time = state.playerState.timers.get(TimerType.INITIAL_LOGIN);
        if (!time) {
            return TimerStateHelper.setTimer(state, TimerType.INITIAL_LOGIN, DateHelper.now);
        }

        const lastLogin = state.playerState.timers.get(TimerType.LAST_LOGIN);
        const lastLoginDay = lastLogin ? DateHelper.getDay(lastLogin) : null;
        if (lastLoginDay && lastLoginDay.getTime() !== DateHelper.today.getTime()) {
            const userLifetime: number = DateHelper.daysSince(time);

            const trackingDay: number = FIRST_LOGIN_DAYS_TO_TRACK.find(level => userLifetime === level);
            if (trackingDay) {
                Game.sdk.gameTracking(FBTrackingEvent.Returning_User, 1, {
                    day: 'D' + trackingDay,
                });
            }
        }

        return state;
    }

    export const claimDailyLoginRewards = (oldState: IGameState, rewards: IRewardData[]): IGameState => {
        let state = {
            ...oldState,
        };
        if (DailyLoginHelper.canCollectDailyLoginReward(state.playerState)) {
            state = PlayerStateHelper.setPlayerState(
                state,
                InventoryStateHelper.receiveRewards(state.playerState, rewards, {
                    queueNext: true,
                })
            );
            state = PlayerStateHelper.setPlayerState(
                state,
                LoginStateHelper.addClaimedDailyLoginReward(state.playerState, {
                    id: DailyLoginHelper.getRewardId(rewards),
                    date: DateHelper.now,
                })
            );
            trackResourceEvent(state, {
                transactionType: TransactionType.REWARD,
                tActionType: TActionType.DAILY_BONUS,
            });
            state = PlayerStateHelper.setPlayerState(
                state,
                FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXType.DAILY_LOGIN])
            );
        } else {
            report('Daily login rewards are not yet available to claim', {
                once: true,
                sampleRate: 0.1,
            });
        }
        return state;
    };

    export const isDailyLoginUnlocked = (state: IGameState): boolean => {
        return Game.player.getHighestLevelCompleted(state) > DAILY_LOGIN_UNLOCK_LEVEL;
    };
}
